/* Collection tabs enhance native Shopify product cards; carts stay in Impulse. */
(() => {
  const editorQuickShopCards = new WeakSet();

  function tabGroups(scope) {
    const groups = [...scope.querySelectorAll('[data-mm-tabs]')];
    if (scope.matches?.('[data-mm-tabs]')) groups.unshift(scope);
    return groups;
  }

  function prepareQuickShop() {
    if (!document.querySelector('[data-mm-tabs]')) return;
    let portal = document.getElementById('mm-quick-shop-modals');
    if (!portal) {
      portal = document.createElement('div');
      portal.id = 'mm-quick-shop-modals';
      document.body.append(portal);
    }
    // Impulse uses product IDs for its shared modals. Keep one per product,
    // outside the hidden collection panels, before native modal initialization.
    // Preserve resident modals on editor reload: their native controllers and
    // fetched product content already refer to these exact DOM nodes.
    const seen = new Map([...portal.querySelectorAll('.modal--quick-shop[id]')]
      .map(modal => [modal.id, modal]));
    document.querySelectorAll('.modal--quick-shop[id]').forEach(modal => {
      const existing = seen.get(modal.id);
      if (existing && existing !== modal) modal.remove();
      else if (!existing) { seen.set(modal.id, modal); portal.append(modal); }
    });
  }

  function initEditorQuickShop(scope) {
    const nativeTheme = window.theme;
    if (!nativeTheme?.settings.quickView || typeof nativeTheme.preloadProductModal !== 'function') return;

    // Impulse initializes cards on page load, but its collection section reload
    // does not. Bind only replacement MMM cards instead of rebinding the page.
    tabGroups(scope).forEach(group => {
      group.querySelectorAll('.grid-product').forEach(card => {
        if (editorQuickShopCards.has(card)) return;
        const button = card.querySelector('.quick-product__btn');
        if (!button || !card.dataset.productId || !card.dataset.productHandle) return;
        editorQuickShopCards.add(card);
        function preload() {
          if (nativeTheme.config.bpSmall) return;
          card.removeEventListener('mouseover', preload);
          card.removeEventListener('focusin', preload);
          nativeTheme.preloadProductModal(card.dataset.productHandle, card.dataset.productId, button);
        }
        card.addEventListener('mouseover', preload);
        card.addEventListener('focusin', preload);
      });
    });
  }

  function init(scope = document) {
    prepareQuickShop();
    tabGroups(scope).forEach(group => {
      if (group.dataset.mmReady) return;
      group.dataset.mmReady = 'true';
      const tabs = [...group.querySelectorAll('[role="tab"]')];
      function select(tab, focus = false) {
        tabs.forEach(item => {
          const active = item === tab;
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
          document.getElementById(item.getAttribute('aria-controls')).hidden = !active;
        });
        if (focus) tab.focus();
        window.dispatchEvent(new Event('resize'));
      }
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); select(tabs[next], true); }
        });
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => {
    init(event.target);
    initEditorQuickShop(event.target);
  });
  document.addEventListener('shopify:block:select', event => {
    const panel = event.target.closest('.mm-tab-panel');
    if (panel) document.getElementById(panel.getAttribute('aria-labelledby'))?.click();
  });
})();

/* Place native status badges below SAMI labels without changing app markup. */
(() => {
  function start() {
    const cardSelector = '.grid-product__content';
    const statusSelector = ':scope > .grid-product__tag--sale, :scope > .grid-product__tag--sold-out';
    const labelSelector = '.samita_productLabel-content';
    const property = '--mm-status-badge-top';
    const cards = new Map();
    const owners = new WeakMap();
    const dirty = new Set();
    let frame = 0;
    let needsSync = false;

    const resizeObserver = new ResizeObserver(entries => {
      entries.forEach(({ target }) => {
        const card = owners.get(target);
        if (card) dirty.add(card);
      });
      schedule();
    });

    function schedule(sync = false) {
      needsSync = needsSync || sync;
      if (!frame) frame = requestAnimationFrame(flush);
    }

    function syncCards() {
      const current = new Set([...document.querySelectorAll(cardSelector)]
        .filter(card => card.querySelector(statusSelector)));

      cards.forEach((labels, card) => {
        if (current.has(card)) return;
        resizeObserver.unobserve(card);
        owners.delete(card);
        labels.forEach(label => {
          resizeObserver.unobserve(label);
          owners.delete(label);
        });
        card.style.removeProperty(property);
        cards.delete(card);
        dirty.delete(card);
      });

      current.forEach(card => {
        let labels = cards.get(card);
        if (!labels) {
          labels = new Set();
          cards.set(card, labels);
          owners.set(card, card);
          resizeObserver.observe(card);
        }
        const present = new Set([...card.querySelectorAll(labelSelector)]
          .filter(label => label.closest(cardSelector) === card));
        labels.forEach(label => {
          if (present.has(label)) return;
          resizeObserver.unobserve(label);
          owners.delete(label);
          labels.delete(label);
        });
        present.forEach(label => {
          if (labels.has(label)) return;
          labels.add(label);
          owners.set(label, card);
          resizeObserver.observe(label);
        });
        dirty.add(card);
      });
    }

    function flush() {
      frame = 0;
      if (needsSync) {
        needsSync = false;
        syncCards();
      }
      const updates = [];
      dirty.forEach(card => {
        if (!card.isConnected || !cards.has(card)) return;
        const cardRect = card.getBoundingClientRect();
        let bottom = 0;
        if (cardRect.width && cardRect.height) {
          cards.get(card).forEach(label => {
            const rect = label.getBoundingClientRect();
            const visibility = getComputedStyle(label).visibility;
            if (!rect.width || !rect.height || visibility === 'hidden' || visibility === 'collapse') return;
            bottom = Math.max(bottom, rect.bottom - cardRect.top);
          });
        }
        updates.push([card, bottom > 0 ? Math.ceil(bottom + 6) + 'px' : '']);
      });
      dirty.clear();
      // Read all geometry before writing styles; our observer ignores attributes.
      updates.forEach(([card, value]) => {
        if (card.style.getPropertyValue(property) === value) return;
        if (value) card.style.setProperty(property, value);
        else card.style.removeProperty(property);
      });
    }

    const changedSelector = cardSelector + ', ' + labelSelector + ', .grid-product__tag--sale, .grid-product__tag--sold-out';
    const mutationObserver = new MutationObserver(records => {
      const relevant = records.some(record =>
        record.target instanceof Element && record.target.closest(cardSelector) ||
        [...record.addedNodes, ...record.removedNodes].some(node => node instanceof Element &&
          (node.matches(changedSelector) || node.querySelector(changedSelector))));
      if (relevant) schedule(true);
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    schedule(true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

/* Card previews load only in view; the native product photo remains the fallback. */
(() => {
  if (!window.customElements || customElements.get('mm-product-card-video')) return;

  class MMProductCardVideo extends HTMLElement {
    constructor() {
      super();
      this._connected = false;
      this._visible = false;
      this._failed = false;
      this._video = null;
      this._attempt = 0;
      this._pendingAttempt = null;
      this._sync = this._syncPlayback.bind(this);
      this._onPlaying = () => {
        if (this._canPlay()) this.setAttribute('data-playing', '');
        else this._stop();
      };
      this._onError = () => {
        this._failed = true;
        this._stop();
      };
    }

    connectedCallback() {
      if (this._connected || !('IntersectionObserver' in window)) return;
      this._connected = true;
      this._visible = false;
      this.removeAttribute('data-playing');
      this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (this._motion.addEventListener) this._motion.addEventListener('change', this._sync);
      else this._motion.addListener(this._sync);
      this._connection?.addEventListener?.('change', this._sync);
      document.addEventListener('visibilitychange', this._sync);
      if (this._video) this._listenToVideo();

      this._observer = new IntersectionObserver(entries => {
        if (!this._connected) return;
        for (const entry of entries) {
          if (entry.target !== this) continue;
          this._visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
          this._syncPlayback();
        }
      }, { threshold: [0, 0.15] });
      this._observer.observe(this);
    }

    disconnectedCallback() {
      this._connected = false;
      this._visible = false;
      this._stop();
      this._observer?.disconnect();
      this._observer = null;
      document.removeEventListener('visibilitychange', this._sync);
      if (this._motion?.removeEventListener) this._motion.removeEventListener('change', this._sync);
      else this._motion?.removeListener(this._sync);
      this._connection?.removeEventListener?.('change', this._sync);
      this._video?.removeEventListener('playing', this._onPlaying);
      this._video?.removeEventListener('error', this._onError);
    }

    _canPlay() {
      return this._connected && this.isConnected && this._visible && !this._failed &&
        !document.hidden && !this._motion?.matches && !this._connection?.saveData;
    }

    _listenToVideo() {
      this._video.addEventListener('playing', this._onPlaying);
      this._video.addEventListener('error', this._onError);
    }

    _hydrate() {
      if (this._video) return true;
      const source = this.querySelector('template')?.content.querySelector('video');
      if (!source) return false;
      const video = source.cloneNode(true);
      // Set properties before insertion/playback, including Safari's inline flag.
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.controls = false;
      video.autoplay = false;
      video.loop = true;
      video.preload = 'none';
      video.tabIndex = -1;
      video.setAttribute('aria-hidden', 'true');
      this._video = video;
      this._listenToVideo();
      this.append(video);
      return true;
    }

    _stop() {
      this._attempt += 1;
      this._pendingAttempt = null;
      this.removeAttribute('data-playing');
      this._video?.pause();
    }

    _syncPlayback() {
      if (!this._canPlay()) {
        this._stop();
        return;
      }
      if (!this._hydrate() || this._pendingAttempt !== null || !this._video.paused) return;
      const video = this._video;
      const attempt = ++this._attempt;
      this._pendingAttempt = attempt;
      let playback;
      try {
        playback = video.play();
      } catch (_) {
        this._pendingAttempt = null;
        this.removeAttribute('data-playing');
        video.pause();
        return;
      }
      Promise.resolve(playback).then(() => {
        // A pending play can resolve after scrolling away or removing the card.
        // Do not interrupt a newer play request if the same card is visible again.
        if (!this._canPlay()) video.pause();
      }).catch(() => {
        if (this._attempt !== attempt) return;
        this.removeAttribute('data-playing');
        video.pause();
      }).finally(() => {
        if (this._pendingAttempt === attempt) this._pendingAttempt = null;
      });
    }
  }

  customElements.define('mm-product-card-video', MMProductCardVideo);
})();
